import { Helmet } from 'react-helmet';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  noindex?: boolean;
  nofollow?: boolean;
}

const DEFAULT_SEO = {
  siteName: 'Shock Value Media',
  defaultTitle: 'Shock Value Media - AI Operators and Organized Execution',
  defaultDescription: 'Shock Value Media helps businesses, founders, and ecosystem partners build AI operators, automate workflows, rescue unfinished apps, evaluate ventures, and turn opportunities into organized execution.',
  defaultImage: '/og-image.png',
  baseUrl: 'https://shockvaluemedia.com',
  twitterHandle: '@ShockValueMedia',
};

export const SEO = ({
  title,
  description = DEFAULT_SEO.defaultDescription,
  keywords = [],
  image = DEFAULT_SEO.defaultImage,
  url,
  type = 'website',
  article,
  noindex = false,
  nofollow = false,
}: SEOProps) => {
  const fullTitle = title
    ? `${title} | ${DEFAULT_SEO.siteName}`
    : DEFAULT_SEO.defaultTitle;

  const fullUrl = url
    ? `${DEFAULT_SEO.baseUrl}${url}`
    : DEFAULT_SEO.baseUrl;

  const fullImage = image.startsWith('http')
    ? image
    : `${DEFAULT_SEO.baseUrl}${image}`;

  const keywordsString = keywords.length > 0
    ? keywords.join(', ')
    : 'AI operators, business systems audit, app rescue, venture evaluation, workflow automation, organized execution, Shock Value Media';

  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robotsString = robotsContent.length > 0
    ? robotsContent.join(',')
    : 'index,follow';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordsString} />
      <meta name="robots" content={robotsString} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:creator" content={DEFAULT_SEO.twitterHandle} />

      {/* Article Specific Tags */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags && article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Additional SEO Tags */}
      <meta name="author" content="Shock Value Media" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content="en" />
      <meta name="language" content="English" />

      {/* Schema.org for Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": type === 'article' ? 'Article' : 'Organization',
          name: DEFAULT_SEO.siteName,
          url: fullUrl,
          logo: `${DEFAULT_SEO.baseUrl}/icon-144x144.png`,
          description: description,
          ...(type === 'article' && article ? {
            headline: title,
            image: fullImage,
            datePublished: article.publishedTime,
            dateModified: article.modifiedTime,
            author: {
              "@type": "Person",
              name: article.author,
            },
          } : {}),
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
